import React, {useEffect} from 'react';
import {Form, Input, Button, Switch} from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import { LeftCircleOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useLoading } from '../../context/LoadingContext';
import { toastError, toastSuccess } from '../../utils/toast';
import {apiUpdatePartner} from "../../api";

const INPUT_STYLE = {
    width: '90%',
};

const ICON_STYLE = {
    marginLeft: 8,
    fontSize: '16px',
    color: '#999'
};

interface EditPartnerForm {
    id: string;
    cpCode: string;
    name: string;
    discount: number | string;
    discountUpgrade: number | string;
    emailsReport: string[];
    ccEmailsReport: string[];
    bccEmailsReport: string[];
    status: 'Active' | 'Inactive' | boolean;
    otpConfirm: string;
}

const rules = {
    OTPConfirm: [
        { required: true, message: 'Vui lòng nhập mã code đăng nhập' },
        { min: 6, message: 'Mã code đăng nhập phải có 6 ký tự' },
    ],
    name: [
        { required: true, message: 'Vui lòng nhập tên' }
    ],
    discount: [
        { required: true, message: 'Vui lòng nhập chiết khấu' }
    ],
    discountUpgrade: [
        { required: true, message: 'Vui lòng nhập chiết khấu nâng cấp' }
    ]
};

export default function EditPartner() {
    const [form] = Form.useForm<EditPartnerForm>();
    const navigate = useNavigate();
    const { setIsLoading } = useLoading();
    const location = useLocation();
    const customer: EditPartnerForm = location.state?.customer;

    useEffect(() => {
        if (!customer.emailsReport?.length) customer.emailsReport = [''];
        if (!customer.ccEmailsReport?.length) customer.ccEmailsReport = [''];
        if (!customer.bccEmailsReport?.length) customer.bccEmailsReport = [''];
        customer.status = customer.status === 'Active';
        form.setFieldsValue(customer);
    }, [customer]);

    const onFinish = async (values: EditPartnerForm) => {
        try {
            setIsLoading(true);
            let req = {...values};
            req.cpCode = customer.cpCode;
            req.emailsReport = cleanArray(values.emailsReport || []);
            req.ccEmailsReport = cleanArray(values.ccEmailsReport || []);
            req.bccEmailsReport = cleanArray(values.bccEmailsReport || []);
            req.status = values.status ? "Active" : "Inactive";
            req.discount = Number(req.discount);
            req.discountUpgrade = Number(req.discountUpgrade);
            const resp = await apiUpdatePartner(req);
            if (resp.status === 200) {
                toastSuccess('Cập nhật dữ liệu thành công!');
            }
        } catch (error) {
            toastError('Vui lòng kiểm tra lại thông tin!')
        } finally {
            setIsLoading(false);
        }
    };

    const cleanArray = (arr: string[]) => {
        return Array.from(new Set(arr.filter(item => item && item.trim() !== '')));

    }

    return (
        <>
            <div style={{display: 'flex', alignItems: 'center', gap: 200, marginBottom: 20}}>
                <Button
                    onClick={() => navigate(-1)}
                    icon={<LeftCircleOutlined/>}
                    style={{background: 'gray', color: 'white'}}
                >
                    Trở lại
                </Button>
                <h2>Đối tác: {customer.cpCode}</h2>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Form
                    style={{ width: '700px' }}
                    form={form}
                    onFinish={onFinish}
                    labelAlign="left"
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                >
                    <Form.Item
                        label="Tên"
                        name="name"
                        rules={rules.name}
                    >
                        <Input
                            placeholder="Nhập tên"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Chiết khấu"
                        name="discount"
                        rules={rules.discount}
                    >
                        <Input
                            placeholder="Nhập chiết khấu"
                            suffix="%"
                            style={INPUT_STYLE}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9.]/g, ''); // chỉ lấy số
                                form.setFieldsValue({ discount: value });
                            }}

                        />
                    </Form.Item>

                    <Form.Item
                        label="Chiết khấu nâng cấp"
                        name="discountUpgrade"
                        rules={rules.discountUpgrade}
                    >
                        <Input
                            placeholder="Nhập chiết khấu nâng cấp"
                            suffix="%"
                            style={INPUT_STYLE}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9.]/g, ''); // chỉ lấy số
                                form.setFieldsValue({ discountUpgrade: value });
                            }}
                        />
                    </Form.Item>

                    <Form.List name="emailsReport" initialValue={['']}>
                        {(fields, { add, remove }) => (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {fields.map((field, index) => (
                                    <Form.Item
                                        label={index === 0 ? "Emails" : " "}
                                        colon={index === 0}
                                        key={field.key}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Form.Item
                                                {...field}
                                                validateTrigger={['onChange', 'onBlur']}
                                                noStyle
                                            >
                                                <Input
                                                    placeholder="Nhập email"
                                                    style={INPUT_STYLE}
                                                />
                                            </Form.Item>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                {fields.length > 1 && (
                                                    <MinusCircleOutlined
                                                        className="dynamic-delete-button"
                                                        onClick={() => remove(field.name)}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                                {index === fields.length - 1 && (
                                                    <PlusOutlined
                                                        onClick={() => add()}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Form.Item>
                                ))}
                            </div>
                        )}
                    </Form.List>

                    <Form.List name="ccEmailsReport" initialValue={['']}>
                        {(fields, { add, remove }) => (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {fields.map((field, index) => (
                                    <Form.Item
                                        label={index === 0 ? "CC Emails" : " "}
                                        colon={index === 0}
                                        key={field.key}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Form.Item
                                                {...field}
                                                validateTrigger={['onChange', 'onBlur']}
                                                noStyle
                                            >
                                                <Input
                                                    placeholder="Nhập email CC"
                                                    style={INPUT_STYLE}
                                                />
                                            </Form.Item>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                {fields.length > 1 && (
                                                    <MinusCircleOutlined
                                                        className="dynamic-delete-button"
                                                        onClick={() => remove(field.name)}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                                {index === fields.length - 1 && (
                                                    <PlusOutlined
                                                        onClick={() => add()}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Form.Item>
                                ))}
                            </div>
                        )}
                    </Form.List>

                    <Form.List name="bccEmailsReport" initialValue={['']}>
                        {(fields, { add, remove }) => (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {fields.map((field, index) => (
                                    <Form.Item
                                        label={index === 0 ? "BCC Emails" : " "}
                                        colon={index === 0}
                                        key={field.key}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Form.Item
                                                {...field}
                                                validateTrigger={['onChange', 'onBlur']}
                                                noStyle
                                            >
                                                <Input
                                                    placeholder="Nhập email BCC"
                                                    style={INPUT_STYLE}
                                                />
                                            </Form.Item>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                {fields.length > 1 && (
                                                    <MinusCircleOutlined
                                                        className="dynamic-delete-button"
                                                        onClick={() => remove(field.name)}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                                {index === fields.length - 1 && (
                                                    <PlusOutlined
                                                        onClick={() => add()}
                                                        style={ICON_STYLE}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Form.Item>
                                ))}
                            </div>
                        )}
                    </Form.List>

                    <Form.Item
                        label="Status"
                        name="status"
                        valuePropName="checked"
                    >
                        <Switch></Switch>
                    </Form.Item>

                    <Form.Item
                        label="Mã OTP xác nhận"
                        name="otpConfirm"
                        rules={rules.OTPConfirm}
                    >
                        <Input
                            placeholder="Nhập OTP xác nhận"
                            style={INPUT_STYLE}
                        />
                    </Form.Item>

                    <Form.Item style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button type="primary" htmlType="submit">
                            Cập nhật
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </>
    );
}
